/**
 * A LOCK WAIT IS WORTH ANOTHER TRY — #8.
 *
 * With `lock_timeout` set below `statement_timeout` (migration 0190), a write
 * blocked on a row lock held by a concurrent transaction fails FAST with
 * SQLSTATE 55P03 ("canceling statement due to lock timeout"), instead of waiting
 * with no ceiling of its own until the 8s statement timeout kills it and reports
 * a misleading "statement timeout". The contention is a moment of overlap on the
 * SAME row, which clears in milliseconds — so the right answer is to wait a
 * short, jittered beat and try again a couple of times before surfacing anything.
 *
 * ONLY 55P03 is retried. That code means the statement acquired no lock and
 * therefore changed NOTHING — it never ran — which is exactly what makes
 * re-running it safe whether it is an upsert, an update or a delete. Any other
 * error is the caller's to see at once, unchanged, on the first attempt.
 *
 * Pulled out of the Supabase seam so the retry can be tested on its own, with a
 * fake runner and no database — see scripts/test-lock-retry.mjs.
 */

/** Postgres SQLSTATE for a statement cancelled because its lock wait timed out. */
export const LOCK_TIMEOUT_CODE = '55P03';

export const isLockTimeout = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as { code?: string }).code === LOCK_TIMEOUT_CODE;

/** The back-off before each RETRY (so N entries = up to N retries after the
 *  first attempt). Short, because row-lock overlap clears in milliseconds. */
export const RETRY_WAITS = [120, 240, 480];

/**
 * Run a PostgREST write; if it comes back a lock-timeout, wait and run it again,
 * up to `waits.length` extra times. `run` must BUILD the query afresh each call
 * (a PostgREST builder is single-shot), and is expected to resolve — not reject —
 * with `{ error }`, as the Supabase client does. `sleep` and `jitter` are
 * injectable for tests; in production they are a real timer and random jitter.
 */
export async function withLockRetry<T extends { error: unknown }>(
  run: () => PromiseLike<T>,
  opts: {
    waits?: number[];
    sleep?: (ms: number) => Promise<void>;
    jitter?: () => number;
  } = {},
): Promise<T> {
  const waits = opts.waits ?? RETRY_WAITS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const jitter = opts.jitter ?? (() => Math.floor(Math.random() * 120));
  for (let attempt = 0; ; attempt += 1) {
    const res = await run();
    if (!res.error || attempt >= waits.length || !isLockTimeout(res.error)) return res;
    await sleep(waits[attempt] + jitter());
  }
}
